import { useMemo } from "react";
import { Point } from "ol/geom";
import { Fill, Icon, Stroke, Style } from "ol/style";
import CircleStyle from "ol/style/Circle";
import { getSpeedColor, interpolateColor } from "../../Helpers/ColorHelper";

// Colours points along the route with a gradient based on each ping's
// position (index) in the session, with dedicated icons for the first/last
// ping. Used as the default point styling.
export const usePositionGradientStyle = () => {
    return useMemo(() => {
        const gradientStyleCache = new window.Map();

        const getGradientStyle = (factor) => {
            const roundedFactor = Math.round(factor * 10) / 10;

            if (!gradientStyleCache.has(roundedFactor)) {
                const startColor = "#dfc7f2";
                const endColor = "#8255a6";

                const fillColor = interpolateColor(endColor, startColor, roundedFactor);
                const strokeColor = "#8255a6"
                const style = new Style({
                    image: new CircleStyle({
                        radius: 4,
                        fill: new Fill({
                            color: fillColor,
                        }),
                        stroke: new Stroke({
                            color: strokeColor,
                            width: 0.25,
                        }),
                        zIndex: factor,
                    })
                });

                gradientStyleCache.set(roundedFactor, style);
            }

            return gradientStyleCache.get(roundedFactor);
        };

        const genericPointStyle = new Style({
            image: new CircleStyle({
                radius: 4,
                fill: new Fill({
                    color: "#8255a6",
                }),
                stroke: new Stroke({
                    color: "#643787",
                    width: 1,
                }),
                zIndex: 999,
            })
        });

        const departureStyle = new Style({
            image: new Icon({
                anchor: [0.5, 20],
                anchorXUnits: "fraction",
                anchorYUnits: "pixels",
                src: "flag.png",
                zIndex: 1000,
            }),
        });

        const arrivalStyle = new Style({
            image: new Icon({
                anchor: [0.5, 20],
                anchorXUnits: "fraction",
                anchorYUnits: "pixels",
                src: "start.png",
                zIndex: 1000,
            }),
        });

        return (feature) => {
            if (feature.getGeometry() instanceof Point) {
                const index = feature.get("index");
                const totalPings = feature.get("totalPings");

                if (totalPings > 2) {
                    if (index === totalPings - 1) {
                        return arrivalStyle;
                    } else if (index === 0) {
                        return departureStyle;
                    } else {
                        const factor = index / (totalPings - 1);
                        return getGradientStyle(factor);
                    }
                } else {
                    return genericPointStyle;
                }
            }
        };
    }, []);
};

// Colours every point on a blue (stationary) to red (fastest ping in the
// session) gradient based on each ping's recorded speed.
export const useSpeedGradientStyle = (maxSpeedKmh) => {
    return useMemo(() => {
        const styleCache = new window.Map();

        const getStyleForSpeed = (speedKmh) => {
            const color = getSpeedColor(speedKmh, maxSpeedKmh);

            if (!styleCache.has(color)) {
                const style = new Style({
                    image: new CircleStyle({
                        radius: 4,
                        fill: new Fill({
                            color,
                        }),
                        stroke: new Stroke({
                            color: "#333333",
                            width: 0.5,
                        }),
                    })
                });

                styleCache.set(color, style);
            }

            return styleCache.get(color);
        };

        return (feature) => {
            if (feature.getGeometry() instanceof Point) {
                return getStyleForSpeed(feature.get("smoothedSpeed"));
            }
        };
    }, [maxSpeedKmh]);
};
